import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    Maximize2,
    Minimize2,
    Shield,
    Loader2,
    FileText,
    AlertTriangle,
    RotateCcw,
    Presentation,
    Layers,
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { renderAsync as renderDocx } from 'docx-preview';
import mammoth from 'mammoth';
import toast from 'react-hot-toast';

// Configure pdfjs worker to reliable CDN matching pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version || '5.6.205'}/pdf.worker.min.mjs`;

interface SecureDocumentViewerProps {
    documentId: number;
    title: string;
    fileType: 'pdf' | 'docx' | 'pptx' | string;
    studentName?: string;
    studentEmail?: string;
    onClose: () => void;
}

export const SecureDocumentViewer: React.FC<SecureDocumentViewerProps> = ({
    documentId,
    title,
    fileType,
    studentName,
    studentEmail,
    onClose,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [zoom, setZoom] = useState(1.0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [pdfDoc, setPdfDoc] = useState<any>(null);
    const [docxHtml, setDocxHtml] = useState<string | null>(null);
    const [slides, setSlides] = useState<string[]>([]);
    const [currentSlide, setCurrentSlide] = useState(1);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const docxContainerRef = useRef<HTMLDivElement>(null);

    const normalizedType = fileType.toLowerCase().replace('.', '');
    const watermarkText = `${studentName || studentEmail || 'AUTHORIZED STUDENT'} • CONFIDENTIAL • LMS PROTECTED VIEW`;

    // Intercept keyboard shortcuts to prevent downloading, printing, inspecting
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Block Ctrl+S / Cmd+S
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                toast.error('Saving or downloading this document is prohibited.', { id: 'save-prohibited' });
            }
            // Block Ctrl+P / Cmd+P
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
                e.preventDefault();
                toast.error('Printing this document is prohibited.', { id: 'print-prohibited' });
            }
            // Escape to exit fullscreen or close
            if (e.key === 'Escape') {
                if (isFullscreen) {
                    setIsFullscreen(false);
                }
            }
            // Arrow keys for navigation
            if (e.key === 'ArrowRight' || e.key === 'PageDown') {
                if (normalizedType === 'pdf') {
                    setCurrentPage(prev => Math.min(totalPages, prev + 1));
                } else if (normalizedType === 'pptx') {
                    setCurrentSlide(prev => Math.min(slides.length || 1, prev + 1));
                }
            }
            if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                if (normalizedType === 'pdf') {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                } else if (normalizedType === 'pptx') {
                    setCurrentSlide(prev => Math.max(1, prev - 1));
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen, totalPages, slides.length, normalizedType]);

    // Fetch document stream securely
    useEffect(() => {
        let isMounted = true;
        const fetchDocument = async () => {
            setLoading(true);
            setError(null);

            const API_URL = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:8000/api';
            const token = localStorage.getItem('token');

            try {
                const response = await fetch(`${API_URL}/student/job-support/${documentId}/view`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: '*/*',
                    },
                });

                if (!response.ok) {
                    if (response.status === 403) {
                        throw new Error('You do not have permission to view this document.');
                    }
                    throw new Error(`Failed to load document (Status ${response.status})`);
                }

                const arrayBuffer = await response.arrayBuffer();
                if (!isMounted) return;

                if (normalizedType === 'pdf') {
                    // Load PDF via pdfjs
                    const loadingTask = pdfjsLib.getDocument({
                        data: new Uint8Array(arrayBuffer),
                        cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version || '5.6.205'}/cmaps/`,
                        cMapPacked: true,
                    });
                    const loadedPdf = await loadingTask.promise;
                    if (!isMounted) return;
                    setPdfDoc(loadedPdf);
                    setTotalPages(loadedPdf.numPages);
                    setCurrentPage(1);
                    setLoading(false);
                } else if (normalizedType === 'docx' || normalizedType === 'doc') {
                    // Try docx-preview renderAsync first, fallback to mammoth HTML
                    setLoading(false);
                    setTimeout(async () => {
                        if (docxContainerRef.current) {
                            try {
                                docxContainerRef.current.innerHTML = '';
                                await renderDocx(arrayBuffer, docxContainerRef.current, undefined, {
                                    className: 'docx-preview-wrapper',
                                    inWrapper: true,
                                    ignoreWidth: false,
                                    ignoreHeight: false,
                                    ignoreFonts: false,
                                    breakPages: true,
                                    useBase64URL: false,
                                });
                            } catch (docxErr) {
                                console.warn('docx-preview fallback to mammoth:', docxErr);
                                try {
                                    const result = await mammoth.convertToHtml({ arrayBuffer });
                                    if (isMounted) {
                                        setDocxHtml(result.value);
                                    }
                                } catch (mammothErr) {
                                    if (isMounted) setError('Could not render Word document formatting.');
                                }
                            }
                        }
                    }, 50);
                } else if (normalizedType === 'pptx' || normalizedType === 'ppt') {
                    // Basic slide text/layout extraction using Mammoth/JSZip or structured presentation viewer
                    try {
                        const JSZip = (await import('jszip')).default;
                        const zip = await JSZip.loadAsync(arrayBuffer);
                        const slideFiles = Object.keys(zip.files).filter(f => f.startsWith('ppt/slides/slide') && f.endsWith('.xml'));
                        
                        slideFiles.sort((a, b) => {
                            const numA = parseInt(a.replace(/[^\d]/g, ''), 10) || 0;
                            const numB = parseInt(b.replace(/[^\d]/g, ''), 10) || 0;
                            return numA - numB;
                        });

                        const extractedSlides: string[] = [];
                        for (const slidePath of slideFiles) {
                            const xmlContent = await zip.files[slidePath].async('string');
                            // Extract text content from XML
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(xmlContent, 'text/xml');
                            const textNodes = doc.getElementsByTagName('a:t');
                            const slideTexts: string[] = [];
                            for (let i = 0; i < textNodes.length; i++) {
                                const text = textNodes[i].textContent?.trim();
                                if (text) slideTexts.push(text);
                            }
                            extractedSlides.push(slideTexts.join('\n\n') || '(Slide content / Graphics)');
                        }

                        if (extractedSlides.length > 0) {
                            setSlides(extractedSlides);
                            setTotalPages(extractedSlides.length);
                        } else {
                            setSlides(['Presentation loaded. Viewing slides in protected mode.']);
                            setTotalPages(1);
                        }
                    } catch (pptxErr) {
                        console.warn('PPTX parse error:', pptxErr);
                        setSlides(['Presentation document ready for viewing.']);
                        setTotalPages(1);
                    }
                    setLoading(false);
                } else {
                    setLoading(false);
                }
            } catch (err: any) {
                if (!isMounted) return;
                console.error('Document fetch/render error:', err);
                setError(err.message || 'An error occurred while preparing the document preview.');
                setLoading(false);
            }
        };

        fetchDocument();

        return () => {
            isMounted = false;
        };
    }, [documentId, normalizedType]);

    // Render PDF page onto canvas when pdfDoc, currentPage, or zoom changes
    const renderPdfPage = useCallback(async () => {
        if (!pdfDoc || !canvasRef.current) return;

        try {
            const page = await pdfDoc.getPage(currentPage);
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (!context) return;

            const viewport = page.getViewport({ scale: zoom * 1.5 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            // Canvas drawing
            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            await page.render(renderContext).promise;
        } catch (renderErr) {
            console.error('PDF Page render error:', renderErr);
        }
    }, [pdfDoc, currentPage, zoom]);

    useEffect(() => {
        if (normalizedType === 'pdf' && pdfDoc) {
            renderPdfPage();
        }
    }, [normalizedType, pdfDoc, currentPage, zoom, renderPdfPage]);

    const handleZoomIn = () => setZoom(prev => Math.min(2.5, +(prev + 0.15).toFixed(2)));
    const handleZoomOut = () => setZoom(prev => Math.max(0.6, +(prev - 0.15).toFixed(2)));
    const handleResetZoom = () => setZoom(1.0);

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            if (containerRef.current?.requestFullscreen) {
                containerRef.current.requestFullscreen().catch(() => {});
            }
            setIsFullscreen(true);
        } else {
            if (document.fullscreenElement && document.exitFullscreen) {
                document.exitFullscreen().catch(() => {});
            }
            setIsFullscreen(false);
        }
    };

    return (
        <div
            ref={containerRef}
            className={`fixed inset-0 z-[99999] flex flex-col bg-slate-950 text-white select-none ${
                isFullscreen ? 'p-0' : 'p-2 sm:p-4 md:p-6 bg-slate-950/90 backdrop-blur-md'
            }`}
            onContextMenu={(e) => {
                e.preventDefault();
                toast('Right-click is disabled on protected documents.', { icon: '🔒' });
            }}
            onCopy={(e) => {
                e.preventDefault();
                toast.error('Copying document content is prohibited.');
            }}
            onDragStart={(e) => e.preventDefault()}
            style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
            {/* Top Toolbar */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-900/95 border border-slate-800 rounded-t-2xl shadow-xl shrink-0">
                {/* Left: Document Info & Protection Badge */}
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                        {normalizedType === 'pptx' ? <Presentation size={18} /> : <FileText size={18} />}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-black text-white truncate max-w-xs sm:max-w-md" title={title}>
                                {title}
                            </h3>
                            <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-slate-800 text-emerald-400 border border-slate-700">
                                {normalizedType.toUpperCase()}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium mt-0.5">
                            <Shield size={12} className="text-emerald-400" />
                            <span className="hidden sm:inline">Protected View &bull; </span>
                            <span className="text-amber-400 font-semibold">Download Restricted</span>
                        </div>
                    </div>
                </div>

                {/* Center: Page / Slide Navigation Controls */}
                <div className="flex items-center gap-1 sm:gap-2">
                    {normalizedType === 'pdf' && totalPages > 1 && (
                        <div className="flex items-center gap-1 bg-slate-800/80 px-2 py-1 rounded-xl border border-slate-700">
                            <button
                                type="button"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-700 transition"
                                title="Previous Page"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-xs font-bold text-slate-300 px-1 font-mono">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-700 transition"
                                title="Next Page"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}

                    {normalizedType === 'pptx' && slides.length > 1 && (
                        <div className="flex items-center gap-1 bg-slate-800/80 px-2 py-1 rounded-xl border border-slate-700">
                            <button
                                type="button"
                                onClick={() => setCurrentSlide(p => Math.max(1, p - 1))}
                                disabled={currentSlide <= 1}
                                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-700 transition"
                                title="Previous Slide"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-xs font-bold text-slate-300 px-1 font-mono">
                                Slide {currentSlide} / {slides.length}
                            </span>
                            <button
                                type="button"
                                onClick={() => setCurrentSlide(p => Math.min(slides.length, p + 1))}
                                disabled={currentSlide >= slides.length}
                                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-700 transition"
                                title="Next Slide"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}

                    {/* Zoom Controls */}
                    <div className="hidden sm:flex items-center gap-1 bg-slate-800/80 px-2 py-1 rounded-xl border border-slate-700">
                        <button
                            type="button"
                            onClick={handleZoomOut}
                            disabled={zoom <= 0.6}
                            className="p-1 text-slate-300 hover:text-white disabled:opacity-30 rounded hover:bg-slate-700 transition"
                            title="Zoom Out"
                        >
                            <ZoomOut size={16} />
                        </button>
                        <span className="text-xs font-bold text-slate-300 px-1 font-mono min-w-[45px] text-center">
                            {Math.round(zoom * 100)}%
                        </span>
                        <button
                            type="button"
                            onClick={handleZoomIn}
                            disabled={zoom >= 2.5}
                            className="p-1 text-slate-300 hover:text-white disabled:opacity-30 rounded hover:bg-slate-700 transition"
                            title="Zoom In"
                        >
                            <ZoomIn size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={handleResetZoom}
                            className="p-1 text-slate-400 hover:text-white rounded hover:bg-slate-700 transition ml-1"
                            title="Reset Zoom"
                        >
                            <RotateCcw size={14} />
                        </button>
                    </div>
                </div>

                {/* Right: Fullscreen and Close */}
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="p-2 text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 transition"
                        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 text-rose-400 hover:text-white bg-rose-950/40 hover:bg-rose-600 rounded-xl border border-rose-800/50 transition cursor-pointer"
                        title="Close Viewer"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Main Document Content Area with Watermark Overlay */}
            <div className="relative flex-1 bg-slate-900 border-x border-b border-slate-800 rounded-b-2xl overflow-auto flex items-center justify-center p-4 sm:p-8">
                {/* Dynamic Floating Watermark Layer */}
                <div
                    className="absolute inset-0 pointer-events-none z-30 overflow-hidden flex flex-wrap items-center justify-around opacity-15 select-none"
                    aria-hidden="true"
                    style={{
                        transform: 'rotate(-25deg) scale(1.3)',
                        userSelect: 'none',
                    }}
                >
                    {Array.from({ length: 18 }).map((_, idx) => (
                        <div key={idx} className="p-8 text-center text-xs sm:text-sm font-black text-slate-300 tracking-widest whitespace-nowrap">
                            {watermarkText}
                        </div>
                    ))}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex flex-col items-center justify-center gap-3 p-8 text-center z-10">
                        <Loader2 size={36} className="text-emerald-400 animate-spin" />
                        <p className="text-sm font-bold text-slate-300">Decrypting & rendering document securely...</p>
                        <p className="text-xs text-slate-500 font-medium">Downloading is disabled for this material</p>
                    </div>
                )}

                {/* Error State */}
                {!loading && error && (
                    <div className="flex flex-col items-center justify-center gap-4 max-w-md p-6 bg-slate-950 border border-red-500/30 rounded-2xl text-center z-10">
                        <div className="w-12 h-12 rounded-2xl bg-red-500/10 text-red-400 flex items-center justify-center">
                            <AlertTriangle size={24} />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-base font-bold text-white">Document Load Restricted</h4>
                            <p className="text-xs text-slate-400 leading-relaxed">{error}</p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition border border-slate-700"
                        >
                            Close Viewer
                        </button>
                    </div>
                )}

                {/* PDF Viewer Canvas */}
                {!loading && !error && normalizedType === 'pdf' && (
                    <div
                        className="transition-transform duration-200 origin-top shadow-2xl rounded-lg overflow-hidden bg-white z-10 max-w-full"
                        style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                    >
                        <canvas ref={canvasRef} className="block max-w-full h-auto" />
                    </div>
                )}

                {/* DOCX Viewer Container */}
                {!loading && !error && (normalizedType === 'docx' || normalizedType === 'doc') && (
                    <div
                        className="w-full max-w-4xl min-h-[500px] bg-white text-slate-900 rounded-2xl shadow-2xl p-6 sm:p-12 overflow-y-auto z-10 transition-transform duration-200"
                        style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                    >
                        <div ref={docxContainerRef} className="docx-container prose max-w-none text-slate-900" />
                        {docxHtml && (
                            <div
                                dangerouslySetInnerHTML={{ __html: docxHtml }}
                                className="prose max-w-none text-slate-900 leading-relaxed"
                            />
                        )}
                    </div>
                )}

                {/* PPTX Presentation Slide Viewer */}
                {!loading && !error && (normalizedType === 'pptx' || normalizedType === 'ppt') && (
                    <div
                        className="w-full max-w-4xl aspect-[16/9] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-700 rounded-3xl shadow-2xl p-8 sm:p-12 flex flex-col justify-between z-10 transition-transform duration-200 relative overflow-hidden"
                        style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                    >
                        {/* Slide Header */}
                        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                            <div className="flex items-center gap-2 text-xs font-black text-emerald-400 uppercase tracking-widest">
                                <Layers size={14} /> Slide {currentSlide} of {slides.length}
                            </div>
                            <span className="text-[10px] text-slate-500 font-mono">
                                {title}
                            </span>
                        </div>

                        {/* Slide Content */}
                        <div className="my-auto py-6 text-slate-100 text-sm sm:text-base leading-relaxed whitespace-pre-line font-medium overflow-y-auto max-h-[360px] pr-2">
                            {slides[currentSlide - 1] || 'Slide Content'}
                        </div>

                        {/* Slide Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-800 text-xs text-slate-400">
                            <span>LGL Project Controls Training &bull; Job Support</span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setCurrentSlide(p => Math.max(1, p - 1))}
                                    disabled={currentSlide <= 1}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-30 rounded-lg text-white font-bold transition flex items-center gap-1"
                                >
                                    <ChevronLeft size={14} /> Prev
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentSlide(p => Math.min(slides.length, p + 1))}
                                    disabled={currentSlide >= slides.length}
                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 rounded-lg text-white font-bold transition flex items-center gap-1"
                                >
                                    Next <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
